/**
 * Slash command implementations:
 *  /model   — interactive provider + model switcher (live, no restart needed)
 *  /compact — manual context summarization
 *  /history — display styled conversation history
 *  /help    — show all available commands
 */

import type { Interface as ReadlineInterface } from 'node:readline';
import type { Agent } from '@vexalyn/agent';
import { createProvider } from '@vexalyn/ai';
import { loadConfig } from '@vexalyn/config';
import { loadEnvFiles, readMergedEnvText } from './env-loader.js';

type AIProvider = ReturnType<typeof createProvider>;

// ─── ANSI helpers ─────────────────────────────────────────────────────────────
const DIM = '\x1b[2m';
const BOLD = '\x1b[1m';
const RESET = '\x1b[0m';
const PURPLE = '\x1b[38;2;168;85;247m';
const YELLOW = '\x1b[33m';
const GREEN = '\x1b[1;92m';
const CYAN = '\x1b[36m';
const GREY = '\x1b[90m';
const WHITE = '\x1b[97m';

const cWidth = 44;
const ANSI_ESCAPE = /\x1b\[[0-?]*[ -\/]*[@-~]/g;

function displayWidth(value: string): number {
  const plain = value.replace(ANSI_ESCAPE, '');
  let width = 0;

  for (const character of plain) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (
      (codePoint >= 0x0300 && codePoint <= 0x036f) ||
      (codePoint >= 0xfe00 && codePoint <= 0xfe0f) ||
      codePoint === 0x200d
    ) continue;
    if (
      codePoint >= 0x1100 &&
      (codePoint <= 0x115f || codePoint === 0x2329 || codePoint === 0x232a ||
        (codePoint >= 0x2e80 && codePoint <= 0xa4cf) ||
        (codePoint >= 0xac00 && codePoint <= 0xd7a3) ||
        (codePoint >= 0xf900 && codePoint <= 0xfaff) ||
        (codePoint >= 0x1f300 && codePoint <= 0x1faff))
    ) {
      width += 2;
    } else {
      width++;
    }
  }

  return width;
}

function cardLine(content: string, width = cWidth): string {
  const pad = Math.max(0, width - displayWidth(content));
  return `${PURPLE}│${RESET} ${content}${' '.repeat(pad)} ${PURPLE}│${RESET}`;
}

function wrapText(text: string, width: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    if (displayWidth(word) > width) {
      if (current) {
        lines.push(current);
        current = '';
      }
      for (let i = 0; i < word.length; i += width) lines.push(word.slice(i, i + width));
      continue;
    }

    const next = current ? `${current} ${word}` : word;
    if (displayWidth(next) > width) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }

  if (current) lines.push(current);
  return lines;
}

function border(char: '╭' | '╰' | '├', width = cWidth): string {
  const line = '─'.repeat(width + 2);
  const close = char === '╭' ? '╮' : char === '╰' ? '╯' : '┤';
  return `${PURPLE}${char}${line}${close}${RESET}`;
}

function menuWidth(title: string, rows: string[]): number {
  return Math.max(cWidth, displayWidth(title), ...rows.map(displayWidth));
}

function clearRenderedMenu(lineCount: number): void {
  if (lineCount === 0) return;
  process.stdout.write(`\x1b[${lineCount}A`);
  for (let line = 0; line < lineCount; line++) {
    process.stdout.write('\r\x1b[2K');
    if (line < lineCount - 1) process.stdout.write('\n');
  }
  process.stdout.write(`\x1b[${lineCount - 1}A\r`);
}

function replaceRenderedMenuRow(lineCount: number, rowIndex: number, row: string): void {
  const linesUp = lineCount - rowIndex;
  process.stdout.write(`\x1b[${linesUp}A\r\x1b[2K${row}`);
  process.stdout.write(`\x1b[${linesUp}B\r`);
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- retained for external palette plugins
const _keepReplaceRow = replaceRenderedMenuRow;

// ─── Known providers and their popular models ──────────────────────────────────
export type ProviderName =
  | 'openai' | 'gemini' | 'openrouter' | 'groq' | 'ollama' | 'qwen'
  | 'deepseek' | 'zai' | 'moonshot' | 'anthropic' | 'mistral' | 'xai'
  | 'cerebras' | 'together' | 'fireworks' | 'deepinfra' | 'siliconflow'
  | 'nvidia' | 'cohere' | 'perplexity' | 'sambanova' | 'novita' | 'ai21';

// Default fallback model lists (used when .env has no model comments)
export const DEFAULT_PROVIDER_MODELS: Record<ProviderName, string[]> = {
  openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'o1-mini', 'o1-preview'],
  gemini: ['gemini-2.0-flash', 'gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-1.5-pro'],
  openrouter: ['anthropic/claude-opus-4.6', 'openai/gpt-5.6', 'deepseek/deepseek-v4-pro', 'google/gemini-3.6-pro'],
  groq: ['llama-4-scout', 'llama-4-maverick', 'llama-3.3-70b-versatile', 'qwen-qwq-32b'],
  ollama: ['llama3.2', 'llama3.1', 'mistral', 'qwen3', 'deepseek-r1'],
  qwen: ['deepseek-v4-pro-0813', 'deepseek-v4-flash', 'qwen3.8-max', 'qwen-plus', 'qwen-turbo', 'qwen-max'],
  deepseek: ['deepseek-v4-pro', 'deepseek-v4-flash', 'deepseek-v3.2', 'deepseek-chat', 'deepseek-reasoner'],
  zai: ['glm-5', 'glm-4.7', 'glm-4.7-flash', 'glm-4.6', 'glm-4.5-air'],
  moonshot: ['kimi-k3', 'kimi-k2.6', 'kimi-k2-thinking', 'kimi-k2-instruct', 'kimi-k2-code'],
  anthropic: ['claude-opus-4.6', 'claude-sonnet-4.6', 'claude-haiku-4.5', 'claude-opus-4.5'],
  mistral: ['mistral-large', 'mistral-medium', 'codestral', 'devstral', 'magistral'],
  xai: ['grok-4.6', 'grok-4.5', 'grok-4', 'grok-4-mini', 'grok-3'],
  cerebras: ['llama-4-scout-17b-16e-instruct', 'llama3.1-70b', 'llama3.1-8b', 'qwen-3-32b'],
  together: ['meta-llama/Llama-4-Maverick', 'Qwen/Qwen3-235B-A22B', 'deepseek-ai/DeepSeek-V3', 'moonshotai/Kimi-K2-Instruct'],
  fireworks: ['accounts/fireworks/models/deepseek-v3', 'accounts/fireworks/models/llama-v3p3-70b-instruct'],
  deepinfra: ['deepseek-ai/DeepSeek-V3', 'Qwen/Qwen3-235B-A22B', 'moonshotai/Kimi-K2-Instruct'],
  siliconflow: ['deepseek-ai/DeepSeek-V3', 'Qwen/Qwen3-235B-A22B', 'THUDM/GLM-4.5'],
  nvidia: ['deepseek-ai/deepseek-v4-flash', 'moonshotai/kimi-k2-instruct', 'meta/llama-3.3-70b-instruct'],
  cohere: ['command-a', 'command-r-plus', 'command-r', 'command-r7b'],
  perplexity: ['sonar', 'sonar-pro', 'sonar-reasoning', 'sonar-reasoning-pro'],
  sambanova: ['DeepSeek-V3', 'DeepSeek-R1', 'Llama-4-Maverick', 'Llama-4-Scout'],
  novita: ['deepseek/deepseek-v3', 'qwen/qwen3-235b-a22b', 'moonshotai/kimi-k2-instruct'],
  ai21: ['jamba-large', 'jamba-mini', 'jamba-instruct'],
};

export const PROVIDER_CREDENTIAL_KEYS: Partial<Record<ProviderName, string[]>> = {
  openai: ['OPENAI_API_KEY', 'VEXALYN_OPENAI_API_KEY'],
  gemini: ['GOOGLE_GENERATIVE_AI_API_KEY', 'GEMINI_API_KEY', 'GOOGLE_API_KEY', 'VEXALYN_GEMINI_API_KEY'],
  openrouter: ['OPENROUTER_API_KEY', 'VEXALYN_OPENROUTER_API_KEY'],
  groq: ['GROQ_API_KEY', 'VEXALYN_GROQ_API_KEY'],
  qwen: ['QWEN_API_KEY', 'DASHSCOPE_API_KEY', 'VEXALYN_QWEN_API_KEY'],
  deepseek: ['DEEPSEEK_API_KEY', 'VEXALYN_DEEPSEEK_API_KEY'],
  zai: ['ZAI_API_KEY', 'GLM_API_KEY', 'VEXALYN_ZAI_API_KEY'],
  moonshot: ['MOONSHOT_API_KEY', 'KIMI_API_KEY', 'VEXALYN_MOONSHOT_API_KEY'],
  anthropic: ['ANTHROPIC_API_KEY', 'CLAUDE_API_KEY', 'VEXALYN_ANTHROPIC_API_KEY'],
  mistral: ['MISTRAL_API_KEY', 'VEXALYN_MISTRAL_API_KEY'],
  xai: ['XAI_API_KEY', 'GROK_API_KEY', 'VEXALYN_XAI_API_KEY'],
  cerebras: ['CEREBRAS_API_KEY', 'VEXALYN_CEREBRAS_API_KEY'],
  together: ['TOGETHER_API_KEY', 'TOGETHERAI_API_KEY', 'VEXALYN_TOGETHER_API_KEY'],
  fireworks: ['FIREWORKS_API_KEY', 'VEXALYN_FIREWORKS_API_KEY'],
  deepinfra: ['DEEPINFRA_API_KEY', 'VEXALYN_DEEPINFRA_API_KEY'],
  siliconflow: ['SILICONFLOW_API_KEY', 'VEXALYN_SILICONFLOW_API_KEY'],
  nvidia: ['NVIDIA_NIM_API_KEY', 'NVIDIA_API_KEY', 'VEXALYN_NVIDIA_API_KEY'],
  cohere: ['COHERE_API_KEY', 'VEXALYN_COHERE_API_KEY'],
  perplexity: ['PERPLEXITY_API_KEY', 'VEXALYN_PERPLEXITY_API_KEY'],
  sambanova: ['SAMBANOVA_API_KEY', 'VEXALYN_SAMBANOVA_API_KEY'],
  novita: ['NOVITA_API_KEY', 'VEXALYN_NOVITA_API_KEY'],
  ai21: ['AI21_API_KEY', 'VEXALYN_AI21_API_KEY'],
};

export function hasConfiguredProvider(provider: ProviderName): boolean {
  if (provider === 'ollama') return Boolean(process.env.OLLAMA_BASE_URL);
  const keyNames = PROVIDER_CREDENTIAL_KEYS[provider] ?? [];
  return keyNames.some((keyName) => {
    const value = process.env[keyName]?.trim();
    return Boolean(value && !/^PASTE_.*_HERE$/i.test(value));
  });
}

export const PROVIDER_LABELS: Record<ProviderName, string> = {
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  openrouter: 'OpenRouter',
  groq: 'Groq',
  ollama: 'Ollama (Local)',
  qwen: 'Qwen',
  deepseek: 'DeepSeek',
  zai: 'Z.AI / GLM',
  moonshot: 'Kimi / Moonshot AI',
  anthropic: 'Anthropic / Claude',
  mistral: 'Mistral AI',
  xai: 'xAI / Grok',
  cerebras: 'Cerebras',
  together: 'Together AI',
  fireworks: 'Fireworks AI',
  deepinfra: 'DeepInfra',
  siliconflow: 'SiliconFlow',
  nvidia: 'NVIDIA NIM',
  cohere: 'Cohere',
  perplexity: 'Perplexity',
  sambanova: 'SambaNova',
  novita: 'Novita AI',
  ai21: 'AI21 Labs',
};

// Provider name aliases — maps section headers in .env to ProviderName keys
const PROVIDER_ALIASES: Record<string, ProviderName> = {
  'qwen': 'qwen', 'dashscope': 'qwen',
  'openai': 'openai',
  'gemini': 'gemini', 'google': 'gemini',
  'openrouter': 'openrouter',
  'groq': 'groq',
  'ollama': 'ollama',
  'deepseek': 'deepseek',
  'zai': 'zai', 'glm': 'zai',
  'moonshot': 'moonshot', 'kimi': 'moonshot',
  'anthropic': 'anthropic', 'claude': 'anthropic',
  'mistral': 'mistral',
  'xai': 'xai', 'grok': 'xai',
  'cerebras': 'cerebras',
  'together': 'together',
  'fireworks': 'fireworks',
  'deepinfra': 'deepinfra',
  'siliconflow': 'siliconflow',
  'nvidia': 'nvidia', 'nim': 'nvidia',
  'cohere': 'cohere',
  'perplexity': 'perplexity',
  'sambanova': 'sambanova',
  'novita': 'novita',
  'ai21': 'ai21',
};

// ─── Model entry with optional tier tag ──────────────────────────────────────
interface ModelEntry {
  name: string;
  tier: 'pro' | 'free' | null;
}

/**
 * Parse model lists from .env comments.
 * Supports tier tags: [PRO] and [FREE] after the model name.
 *
 *   # Models:
 *   #   - deepseek-v4-pro-0813  [PRO]
 *   #   - deepseek-v4-flash     [FREE]
 *
 * Falls back to DEFAULT_PROVIDER_MODELS for any provider not found.
 */
function parseModelsFromEnv(): Record<ProviderName, ModelEntry[]> {
  const envContent = readMergedEnvText(process.cwd(), import.meta.url);

  if (!envContent) {
    return Object.fromEntries(
      Object.entries(toModelEntries(DEFAULT_PROVIDER_MODELS))
        .filter(([provider]) => hasConfiguredProvider(provider as ProviderName))
    ) as Record<ProviderName, ModelEntry[]>;
  }

  const result: Partial<Record<ProviderName, ModelEntry[]>> = {};
  let currentProvider: ProviderName | null = null;
  let inModelsBlock = false;

  for (const raw of envContent.split('\n')) {
    const line = raw.trim();
    if (!line.startsWith('#')) continue;

    // Model entry: "#   - model-name [TAG]"
    if (/^#\s+-\s+\S/.test(line)) {
      if (inModelsBlock && currentProvider) {
        const rest = line.replace(/^#\s+-\s+/, '').trim();
        const tagMatch = rest.match(/\[(PRO|FREE)\]\s*$/i);
        const modelName = rest.replace(/\[(PRO|FREE)\]\s*$/i, '').trim();
        const tier: ModelEntry['tier'] = tagMatch
          ? (tagMatch[1]!.toLowerCase() as 'pro' | 'free')
          : null;
        if (modelName) {
          if (!result[currentProvider]) result[currentProvider] = [];
          result[currentProvider]!.push({ name: modelName, tier });
        }
      }
      continue;
    }

    if (/models\s*:/i.test(line)) { if (currentProvider) inModelsBlock = true; continue; }
    if (/^#[\s\-=─]+$/.test(line)) continue;

    // If we're inside a models block, check if this is a new provider header.
    // If yes — end the current block and start a new provider.
    // If no  — it's a sub-section comment (e.g. "# General / Agent"), ignore it.
    if (inModelsBlock) {
      const withoutUrlInBlock = line.replace(/https?:\/\/\S+/gi, '').trim();
      const headerInBlock = withoutUrlInBlock.replace(/^#+\s*/, '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').trim();
      const wordsInBlock = headerInBlock.split(/\s+/).filter(Boolean);
      const foundInBlock = Object.keys(PROVIDER_ALIASES).find(a => wordsInBlock[0] === a || wordsInBlock[1] === a);
      if (foundInBlock) { currentProvider = PROVIDER_ALIASES[foundInBlock]!; inModelsBlock = false; }
      continue;
    }

    const withoutUrl = line.replace(/https?:\/\/\S+/gi, '').trim();
    const headerText = withoutUrl.replace(/^#+\s*/, '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').trim();
    const words = headerText.split(/\s+/).filter(Boolean);
    const found = Object.keys(PROVIDER_ALIASES).find(a => words[0] === a || words[1] === a);
    if (found) { currentProvider = PROVIDER_ALIASES[found]!; inModelsBlock = false; }
  }

  const merged = toModelEntries(DEFAULT_PROVIDER_MODELS);
  for (const [provider, models] of Object.entries(result) as [ProviderName, ModelEntry[]][]) {
    if (models.length > 0) merged[provider] = models;
  }
  return Object.fromEntries(
    Object.entries(merged).filter(([provider]) => hasConfiguredProvider(provider as ProviderName))
  ) as Record<ProviderName, ModelEntry[]>;
}

/** Convert plain string model lists to ModelEntry[] (no tier tag) */
function toModelEntries(src: Record<ProviderName, string[]>): Record<ProviderName, ModelEntry[]> {
  return Object.fromEntries(
    Object.entries(src).map(([p, models]) => [p, models.map(name => ({ name, tier: null }))])
  ) as Record<ProviderName, ModelEntry[]>;
}

// ─── Raw keypress reader (arrow keys + fuzzy filter input, ala opencode palette) ───────
type KeyEvent = 'up' | 'down' | 'enter' | 'escape' | 'backspace' | { char: string };
function readKey(): Promise<KeyEvent> {
  return new Promise((resolve) => {
    const handler = (buf: Buffer) => {
      const key = buf.toString();
      if (key === '\x1b[A' || key === '\x1b[D') { process.stdin.removeListener('data', handler); resolve('up'); }
      else if (key === '\x1b[B' || key === '\x1b[C') { process.stdin.removeListener('data', handler); resolve('down'); }
      else if (key === '\r' || key === '\n') { process.stdin.removeListener('data', handler); resolve('enter'); }
      else if (key === '\x1b' || key === 'q') { process.stdin.removeListener('data', handler); resolve('escape'); }
      else if (key === '\x7f' || key === '\x08') { process.stdin.removeListener('data', handler); resolve('backspace'); }
      else if (key.length === 1 && key >= ' ' && key <= '~') { process.stdin.removeListener('data', handler); resolve({ char: key }); }
      else { process.stdin.removeListener('data', handler); resolve({ char: '' }); }
    };
    process.stdin.setRawMode(true);
    process.stdin.resume();
    process.stdin.on('data', handler);
  });
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for backwards compat with legacy callers
function readArrowKey(): Promise<'up' | 'down' | 'enter' | 'escape'> {
  return readKey().then(k => {
    if (typeof k === 'object') return 'enter' as const;
    if (k === 'backspace') return 'escape' as const;
    return k as 'up' | 'down' | 'enter' | 'escape';
  });
}

// ─── Badge renderer ───────────────────────────────────────────────────────────
function renderBadge(tier: ModelEntry['tier']): string {
  if (tier === 'pro') return `  \x1b[38;2;255;215;0m\x1b[1m◆ PRO\x1b[0m`;   // gold diamond + PRO
  if (tier === 'free') return `  \x1b[1;92m✦ FREE\x1b[0m`;                     // green spark + FREE
  return '';
}

/** Render a vertical selection menu for plain string items. Returns selected index or -1.
 *  Ketik untuk filter fuzzy ala opencode palette - langsung ketik substring untuk mempersempit list. */
async function selectMenu(title: string, items: string[], currentIdx = 0): Promise<number> {
  let filter = '';
  const getFiltered = (): { item: string; origIdx: number }[] => {
    if (!filter) return items.map((item, i) => ({ item, origIdx: i }));
    const low = filter.toLowerCase();
    return items.map((item, i) => ({ item, origIdx: i })).filter(o => o.item.toLowerCase().includes(low));
  };
  let filtered = getFiltered();
  let sel = Math.max(0, Math.min(currentIdx, filtered.length - 1));
  if (filter) sel = 0;
  const computeWidth = () => menuWidth(title, filtered.length ? filtered.map(f => `▶ ${f.item}`) : [`(tidak ada hasil)`]) + 2;
  process.stdout.write('\x1b[?25l');
  let lineCount = 0;
  const render = () => {
    const w = computeWidth();
    const filterLine = filter ? `${GREY}filter:${RESET} ${WHITE}${filter}${RESET}${GREY}█${RESET}` : `${GREY}ketik untuk filter...${RESET}`;
    // rebuild fully tiap render biar filter akurat (ala opencode - redraw penuh)
    if (lineCount > 0) clearRenderedMenu(lineCount);
    const lines: string[] = [];
    lines.push(border('╭', w));
    lines.push(cardLine(`${BOLD}${PURPLE}${title}${RESET}`, w));
    lines.push(border('├', w));
    lines.push(cardLine(filterLine, w));
    lines.push(border('├', w));
    filtered.forEach((f, i) => {
      const selected = i === sel;
      const prefix = selected ? `${BOLD}${YELLOW}▶ ${RESET}` : `  ${GREY}`;
      const label = selected ? `${BOLD}${WHITE}${f.item}${RESET}` : `${GREY}${f.item}${RESET}`;
      lines.push(cardLine(`${prefix}${label}`, w));
    });
    if (filtered.length === 0) lines.push(cardLine(`  ${GREY}(tidak ada hasil)${RESET}`, w));
    lines.push(border('╰', w));
    lines.push(`  ${GREY}↑↓ pilih  ketik filter  Enter konfirmasi  Esc batal  Backspace hapus${RESET}`);
    lines.forEach(l => process.stdout.write(l + '\n'));
    lineCount = lines.length;
  };
  render();
  while (true) {
    const key = await readKey();
    if (key === 'up') { sel = filtered.length ? (sel - 1 + filtered.length) % filtered.length : 0; render(); }
    else if (key === 'down') { sel = filtered.length ? (sel + 1) % filtered.length : 0; render(); }
    else if (key === 'enter') {
      process.stdin.setRawMode(false);
      clearRenderedMenu(lineCount);
      process.stdout.write('\x1b[?25h');
      if (filtered.length === 0) return -1;
      return filtered[sel]!.origIdx;
    } else if (key === 'escape') {
      process.stdin.setRawMode(false);
      clearRenderedMenu(lineCount);
      process.stdout.write('\x1b[?25h');
      return -1;
    } else if (key === 'backspace') {
      filter = filter.slice(0, -1);
      filtered = getFiltered();
      sel = 0;
      render();
    } else if (typeof key === 'object' && key.char) {
      filter += key.char;
      filtered = getFiltered();
      sel = 0;
      render();
    }
  }
}

/** Render a vertical selection menu for ModelEntry items with PRO/FREE badges + fuzzy filter. */
async function selectModelMenu(title: string, items: ModelEntry[], currentIdx = 0): Promise<number> {
  let filter = '';
  const getFiltered = (): { item: ModelEntry; origIdx: number }[] => {
    if (!filter) return items.map((it, i) => ({ item: it, origIdx: i }));
    const low = filter.toLowerCase();
    return items.map((it, i) => ({ item: it, origIdx: i })).filter(o => o.item.name.toLowerCase().includes(low));
  };
  let filtered = getFiltered();
  let sel = Math.max(0, Math.min(currentIdx, filtered.length - 1));
  if (filter) sel = 0;
  process.stdout.write('\x1b[?25l');
  let lineCount = 0;
  const render = () => {
    const w = menuWidth(title, filtered.length ? filtered.map(f => `▶ ${f.item.name}${renderBadge(f.item.tier)}`) : ['(tidak ada)']) + 2;
    const filterLine = filter ? `${GREY}filter:${RESET} ${WHITE}${filter}${RESET}${GREY}█${RESET}` : `${GREY}ketik untuk filter...${RESET}`;
    if (lineCount > 0) clearRenderedMenu(lineCount);
    const lines: string[] = [];
    lines.push(border('╭', w));
    lines.push(cardLine(`${BOLD}${PURPLE}${title}${RESET}`, w));
    lines.push(border('├', w));
    lines.push(cardLine(filterLine, w));
    lines.push(border('├', w));
    filtered.forEach((f, i) => {
      const selected = i === sel;
      const prefix = selected ? `${BOLD}${YELLOW}▶ ${RESET}` : `  ${GREY}`;
      const label = selected ? `${BOLD}${WHITE}${f.item.name}${RESET}` : `${GREY}${f.item.name}${RESET}`;
      lines.push(cardLine(`${prefix}${label}${renderBadge(f.item.tier)}`, w));
    });
    if (filtered.length === 0) lines.push(cardLine(`  ${GREY}(tidak ada hasil)${RESET}`, w));
    lines.push(border('╰', w));
    lines.push(`  ${GREY}↑↓ pilih  ketik filter  Enter konfirmasi  Esc batal  Backspace hapus${RESET}`);
    lines.forEach(l => process.stdout.write(l + '\n'));
    lineCount = lines.length;
  };
  render();
  while (true) {
    const key = await readKey();
    if (key === 'up') { sel = filtered.length ? (sel - 1 + filtered.length) % filtered.length : 0; render(); }
    else if (key === 'down') { sel = filtered.length ? (sel + 1) % filtered.length : 0; render(); }
    else if (key === 'enter') {
      process.stdin.setRawMode(false);
      clearRenderedMenu(lineCount);
      process.stdout.write('\x1b[?25h');
      if (filtered.length === 0) return -1;
      return filtered[sel]!.origIdx;
    } else if (key === 'escape') {
      process.stdin.setRawMode(false);
      clearRenderedMenu(lineCount);
      process.stdout.write('\x1b[?25h');
      return -1;
    } else if (key === 'backspace') {
      filter = filter.slice(0, -1);
      filtered = getFiltered();
      sel = 0;
      render();
    } else if (typeof key === 'object' && key.char) {
      filter += key.char;
      filtered = getFiltered();
      sel = 0;
      render();
    }
  }
}

// ─── /model ───────────────────────────────────────────────────────────────────
export interface ModelSwitchResult {
  provider: ProviderName;
  model: string;
  newProvider: AIProvider;
}

export async function runPlanCommand(
  readline: ReadlineInterface,
  currentEnabled: boolean,
): Promise<boolean | null> {
  readline.pause();
  process.stdout.write('\r\x1b[2K\n');

  const items = ['ON  - wajib bikin plan dulu', 'OFF - langsung eksekusi'];
  const selected = await selectMenu('◆ Plan Mode', items, currentEnabled ? 0 : 1);
  readline.resume();

  if (selected === -1) return null;
  const enabled = selected === 0;
  const status = enabled ? `${GREEN}ON${RESET}` : `${YELLOW}OFF${RESET}`;
  const description = enabled
    ? 'Agent wajib membuat plan sebelum mengubah file.'
    : 'Agent langsung eksekusi tanpa plan otomatis.';
  const width = Math.max(cWidth, displayWidth(`  Status   ${enabled ? 'ON' : 'OFF'}`), displayWidth(`  Mode     ${description}`));

  process.stdout.write('\n');
  process.stdout.write(border('╭', width) + '\n');
  process.stdout.write(cardLine(`${BOLD}${PURPLE}◆ Plan Mode Updated${RESET}`, width) + '\n');
  process.stdout.write(border('├', width) + '\n');
  process.stdout.write(cardLine(`  ${GREY}Status${RESET}   ${status}`, width) + '\n');
  for (const line of wrapText(description, Math.max(8, width - displayWidth('  Mode     ')))) {
    process.stdout.write(cardLine(`  ${GREY}Mode${RESET}     ${WHITE}${line}${RESET}`, width) + '\n');
  }
  process.stdout.write(border('╰', width) + '\n\n');

  return enabled;
}

export interface UsageInfo {
  usedTokens: number;
  remainingTokens: number;
  maxTokens: number;
}

export function runUsageCommand(info: UsageInfo): void {
  const percent = info.maxTokens > 0
    ? Math.min(100, Math.round((info.usedTokens / info.maxTokens) * 100))
    : 0;
  const width = Math.max(
    cWidth,
    displayWidth(`  Token terpakai   ${info.usedTokens.toLocaleString('id-ID')}`),
    displayWidth(`  Token tersisa    ${info.remainingTokens.toLocaleString('id-ID')}`),
    displayWidth(`  Batas context    ${info.maxTokens.toLocaleString('id-ID')}`),
  );

  process.stdout.write('\n');
  process.stdout.write(border('╭', width) + '\n');
  process.stdout.write(cardLine(`${BOLD}${PURPLE}◆ Token Usage${RESET}`, width) + '\n');
  process.stdout.write(border('├', width) + '\n');
  process.stdout.write(cardLine(`  ${GREY}Token terpakai${RESET}   ${WHITE}${info.usedTokens.toLocaleString('id-ID')}${RESET}`, width) + '\n');
  process.stdout.write(cardLine(`  ${GREY}Token tersisa${RESET}    ${GREEN}${info.remainingTokens.toLocaleString('id-ID')}${RESET}`, width) + '\n');
  process.stdout.write(cardLine(`  ${GREY}Batas context${RESET}    ${WHITE}${info.maxTokens.toLocaleString('id-ID')}${RESET}`, width) + '\n');
  process.stdout.write(cardLine(`  ${GREY}Pemakaian${RESET}        ${YELLOW}${percent}%${RESET}`, width) + '\n');
  process.stdout.write(border('╰', width) + '\n\n');
}

export async function runModelCommand(
  readline: ReadlineInterface,
  currentProvider: ProviderName,
  currentModel: string,
): Promise<ModelSwitchResult | null> {
  readline.pause();
  process.stdout.write('\r\x1b[2K');
  process.stdout.write('\n');

  // Read .env fresh every time, including edits made after the CLI started.
  loadEnvFiles(process.cwd(), import.meta.url);
  const PROVIDER_MODELS = parseModelsFromEnv();

  const providerNames = Object.keys(PROVIDER_MODELS) as ProviderName[];
  const providerLabels = providerNames.map(p => PROVIDER_LABELS[p]);
  const currentProviderIdx = providerNames.indexOf(currentProvider);

  // Step 1: pick provider
  const providerIdx = await selectMenu('🎛️  Pilih Provider', providerLabels, Math.max(0, currentProviderIdx));
  if (providerIdx === -1) { readline.resume(); return null; }

  const chosenProvider = providerNames[providerIdx]!;
  const models = PROVIDER_MODELS[chosenProvider];
  const currentModelIdx = models.findIndex(m => m.name === currentModel);

  // Step 2: pick model with PRO/FREE badges
  const modelIdx = await selectModelMenu(`Model — ${PROVIDER_LABELS[chosenProvider]}`, models, Math.max(0, currentModelIdx));
  if (modelIdx === -1) { readline.resume(); return null; }

  const chosenEntry = models[modelIdx]!;
  let chosenModel = chosenEntry.name;

  // Warn if PRO model selected
  if (chosenEntry.tier === 'pro') {
    process.stdout.write(`\n  ${'\x1b[38;2;255;215;0m'}\x1b[1m◆ Model PRO${RESET} — pastikan akun & API key kamu support model ini.\n`);
  }

  // Opencode-like variant/effort picker (model-variants.ts) - pilih effort jika model support variant
  const variantOptions = ['default', 'low', 'medium', 'high', 'max', 'xhigh'];
  const showVariant = /reason|thinking|o1|opus|claude|grok|kimi/i.test(chosenModel);
  if (showVariant) {
    const vIdx = await selectMenu('◆ Variant / Effort', variantOptions, 0);
    if (vIdx !== -1 && variantOptions[vIdx] !== 'default') {
      // opencode simpan di opencode.json "variant", kita append ke model string untuk display
      chosenModel = `${chosenModel} [${variantOptions[vIdx]}]`;
    }
  }

  // Create new provider with overridden env
  const config = loadConfig({
    ...process.env,
    VEXALYN_DEFAULT_PROVIDER: chosenProvider,
    VEXALYN_DEFAULT_MODEL: chosenModel,
  });

  let newProvider: AIProvider;
  try {
    newProvider = createProvider(config);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    process.stdout.write(`\n${DIM}│${RESET} ${'\x1b[91m'}✖ Gagal inisialisasi provider: ${msg}${RESET}\n\n`);
    readline.resume();
    return null;
  }

  process.stdout.write('\n');
  const providerLabel = PROVIDER_LABELS[chosenProvider];
  const successWidth = Math.max(cWidth, displayWidth(`  Provider  ${providerLabel}`));
  process.stdout.write(border('╭', successWidth) + '\n');
  process.stdout.write(cardLine(`${GREEN}✔ Provider diganti!${RESET}`, successWidth) + '\n');
  process.stdout.write(cardLine(`  ${GREY}Provider  ${RESET}${CYAN}${providerLabel}${RESET}`, successWidth) + '\n');
  const modelPrefix = `  ${GREY}Model     ${RESET}`;
  const modelIndent = ' '.repeat(displayWidth('  Model     '));
  const modelWidth = Math.max(8, successWidth - displayWidth('  Model     '));
  wrapText(chosenModel, modelWidth).forEach((line, index) => {
    const prefix = index === 0 ? modelPrefix : modelIndent;
    process.stdout.write(cardLine(`${prefix}${CYAN}${line}${RESET}`, successWidth) + '\n');
  });
  process.stdout.write(border('╰', successWidth) + '\n\n');

  readline.resume();
  return { provider: chosenProvider, model: chosenModel, newProvider };
}

// ─── /compact ─────────────────────────────────────────────────────────────────
export async function runCompactCommand(agent: Agent, currentProvider: AIProvider): Promise<void> {
  const messages = agent.history.list();
  const chatMsgs = messages.filter(m => m.role === 'user' || m.role === 'assistant');

  if (chatMsgs.length < 4) {
    const warningTitle = `${YELLOW}⚠ Percakapan terlalu pendek untuk di-compact.${RESET}`;
    const compactWidth = Math.max(cWidth, displayWidth(warningTitle) + 1);
    process.stdout.write('\n');
    process.stdout.write(border('╭', compactWidth) + '\n');
    process.stdout.write(cardLine(warningTitle, compactWidth) + '\n');
    process.stdout.write(cardLine(`  ${GREY}Minimal 4 pesan diperlukan.${RESET}`, compactWidth) + '\n');
    process.stdout.write(border('╰', compactWidth) + '\n\n');
    return;
  }

  const spinner = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];
  let spinIdx = 0;
  process.stdout.write('\x1b[?25l');
  const spinTimer = setInterval(() => {
    process.stdout.write(`\r${PURPLE}${spinner[spinIdx++ % spinner.length]}${RESET} ${GREY}Merangkum percakapan...${RESET}`);
  }, 80);

  try {
    const summaryPrompt = [
      { role: 'system' as const, content: 'Rangkum seluruh percakapan berikut menjadi 2-3 paragraf ringkas dalam Bahasa Indonesia. Sertakan: keputusan penting, file yang dimodifikasi, dan konteks utama. Awali dengan "[Compact Memory]".' },
      ...chatMsgs,
    ];

    let summary = '';
    for await (const chunk of currentProvider.stream(summaryPrompt)) {
      summary += chunk;
    }

    clearInterval(spinTimer);
    process.stdout.write('\r\x1b[K\x1b[?25h');

    // Replace history with summary
    const systemMsg = messages.find(m => m.role === 'system');
    agent.history.clear();
    if (systemMsg) agent.history.add(systemMsg);
    agent.history.add({ role: 'system', content: summary.trim() });

    const lineCount = chatMsgs.length;
    process.stdout.write('\n');
    process.stdout.write(border('╭') + '\n');
    process.stdout.write(cardLine(`${GREEN}✔ Context berhasil di-compact!${RESET}`) + '\n');
    process.stdout.write(cardLine(`  ${GREY}${lineCount} pesan → 1 ringkasan${RESET}`) + '\n');
    process.stdout.write(cardLine(`  ${GREY}Token context sekarang jauh lebih hemat.${RESET}`) + '\n');
    process.stdout.write(border('╰') + '\n\n');
  } catch (err) {
    clearInterval(spinTimer);
    process.stdout.write('\r\x1b[K\x1b[?25h');
    const msg = err instanceof Error ? err.message : String(err);
    process.stdout.write(`\n${'\x1b[91m'}✖ Gagal compact: ${msg}${RESET}\n\n`);
  }
}

// ─── /history ─────────────────────────────────────────────────────────────────
export function runHistoryCommand(agent: Agent): void {
  const messages = agent.history.list().filter(m => m.role === 'user' || m.role === 'assistant');

  process.stdout.write('\n');
  process.stdout.write(border('╭') + '\n');
  process.stdout.write(cardLine(`${BOLD}${PURPLE}✦ Riwayat Percakapan${RESET}`) + '\n');
  process.stdout.write(border('├') + '\n');

  if (messages.length === 0) {
    process.stdout.write(cardLine(`  ${GREY}Belum ada percakapan.${RESET}`) + '\n');
  } else {
    messages.forEach((msg, i) => {
      const isUser = msg.role === 'user';
      const label = isUser ? `${BOLD}${YELLOW}You${RESET}` : `${BOLD}${PURPLE}Agent${RESET}`;
      const num = `${GREY}${String(i + 1).padStart(2)}.${RESET}`;
      // Truncate long messages
      const raw = msg.content.replace(/\x1b\[[0-9;]*m/g, '');
      const preview = raw.length > 72 ? raw.slice(0, 72) + '…' : raw;
      process.stdout.write(cardLine(`${num} ${label}  ${GREY}${preview}${RESET}`) + '\n');
      if (i < messages.length - 1) {
        process.stdout.write(cardLine(`${GREY}${'─'.repeat(cWidth - 2)}${RESET}`) + '\n');
      }
    });
  }

  process.stdout.write(border('├') + '\n');
  process.stdout.write(cardLine(`  ${GREY}Total: ${messages.length} pesan${RESET}`) + '\n');
  process.stdout.write(border('╰') + '\n\n');
}

// ─── Ctrl+P file picker ala opencode ───────────────────────────────────────────
import { readdirSync, statSync } from 'node:fs';
import { relative, join, resolve } from 'node:path';

function collectFiles(dir: string, root: string, out: string[], limit = 2000): void {
  if (out.length >= limit) return;
  let entries: string[] = [];
  try { entries = readdirSync(dir) as unknown as string[]; } catch { return; }
  for (const e of entries) {
    if (out.length >= limit) break;
    if (e === 'node_modules' || e === '.git' || e === 'dist' || e === '.turbo' || e === '.vexalyn') continue;
    if (e.startsWith('.')) continue;
    const full = join(dir, e);
    try {
      const st = statSync(full);
      if (st.isDirectory()) collectFiles(full, root, out, limit);
      else out.push(relative(root, full).replace(/\\/g, '/'));
    } catch {}
  }
}

export async function runFilePicker(readline: ReadlineInterface, workspaceRoot: string): Promise<string | null> {
  readline.pause();
  process.stdout.write('\r\x1b[2K\n');
  const allFiles: string[] = [];
  collectFiles(resolve(workspaceRoot), resolve(workspaceRoot), allFiles);
  if (allFiles.length === 0) {
    process.stdout.write(`${GREY}Tidak ada file ditemukan.${RESET}\n\n`);
    readline.resume();
    return null;
  }
  allFiles.sort();
  let filter = '';
  const getFiltered = (): string[] => {
    if (!filter) return allFiles.slice(0, 80);
    const low = filter.toLowerCase();
    return allFiles.filter(f => f.toLowerCase().includes(low)).slice(0, 80);
  };
  let filtered = getFiltered();
  let sel = 0;
  let lineCount = 0;
  const render = () => {
    const w = Math.max(cWidth + 20, displayWidth('  ' + (filtered[0] ?? '')) + 4);
    const filterLine = filter ? `${GREY}›${RESET} ${WHITE}${filter}${RESET}${GREY}█${RESET}` : `${GREY}ketik nama file untuk filter...${RESET}`;
    if (lineCount > 0) clearRenderedMenu(lineCount);
    const lines: string[] = [];
    lines.push(border('╭', w));
    lines.push(cardLine(`${BOLD}${PURPLE}◆ Quick Open  (Ctrl+P)${RESET}`, w));
    lines.push(border('├', w));
    lines.push(cardLine(filterLine, w));
    lines.push(border('├', w));
    filtered.forEach((f, i) => {
      const selected = i === sel;
      const prefix = selected ? `${BOLD}${YELLOW}▶ ${RESET}` : `  ${GREY}`;
      const label = selected ? `${BOLD}${WHITE}${f}${RESET}` : `${GREY}${f}${RESET}`;
      lines.push(cardLine(`${prefix}${label}`, w));
    });
    if (filtered.length === 0) lines.push(cardLine(`  ${GREY}(tidak ada hasil)${RESET}`, w));
    if (allFiles.length > 80 && !filter) lines.push(cardLine(`  ${GREY}... ${allFiles.length - 80} file lain (ketik untuk filter)${RESET}`, w));
    lines.push(border('╰', w));
    lines.push(`  ${GREY}↑↓ pilih  ketik filter  Enter pilih  Esc batal${RESET}`);
    lines.forEach(l => process.stdout.write(l + '\n'));
    lineCount = lines.length;
  };
  render();
  process.stdout.write('\x1b[?25l');
  while (true) {
    const key = await readKey();
    if (key === 'up') { sel = filtered.length ? (sel - 1 + filtered.length) % filtered.length : 0; render(); }
    else if (key === 'down') { sel = filtered.length ? (sel + 1) % filtered.length : 0; render(); }
    else if (key === 'enter') {
      process.stdin.setRawMode(false);
      clearRenderedMenu(lineCount);
      process.stdout.write('\x1b[?25h');
      readline.resume();
      if (filtered.length === 0) return null;
      return filtered[sel] ?? null;
    } else if (key === 'escape') {
      process.stdin.setRawMode(false);
      clearRenderedMenu(lineCount);
      process.stdout.write('\x1b[?25h');
      readline.resume();
      return null;
    } else if (key === 'backspace') {
      filter = filter.slice(0, -1);
      filtered = getFiltered();
      sel = 0;
      render();
    } else if (typeof key === 'object' && key.char) {
      filter += key.char;
      filtered = getFiltered();
      sel = 0;
      render();
    }
  }
}

// ─── /help ────────────────────────────────────────────────────────────────────
export async function runThemePicker(readline: ReadlineInterface, current: string, themes: string[]): Promise<string | null> {
  const idx = themes.indexOf(current);
  const picked = await selectMenu('◆ Pilih Theme', themes, Math.max(0, idx));
  if (picked === -1) return null;
  return themes[picked] ?? null;
}

export function runHelpCommand(): void {
  const cmds: [string, string][] = [
    ['/model, /models', 'Ganti provider / model AI (fuzzy filter)'],
    ['/compact, /summarize', 'Ringkas percakapan untuk hemat token'],
    ['/plan', 'Aktif/nonaktifkan plan otomatis'],
    ['/stats (/usage)', 'Lihat estimasi token context'],
    ['/history', 'Lihat riwayat percakapan'],
    ['/clear', 'Hapus semua riwayat chat (alias: clear)'],
    ['/new', 'Mulai sesi baru (reset history, tasks, todos)'],
    ['/init', 'Buat AGENTS.md template di project'],
    ['/sessions', 'Daftar session tersimpan'],
    ['/resume', 'Lanjutkan sesi sebelumnya'],
    ['/continue', 'Lanjutkan task pending'],
    ['/details', 'Toggle detail tool verbose'],
    ['/editor', 'Buka editor eksternal ($EDITOR)'],
    ['/export', 'Export session ke Markdown'],
    ['/connect <prov>', 'Tambah provider (-> vexalyn auth login)'],
    ['/share', 'Share session (stub)'],
    ['/unshare', 'Hapus share session'],
    ['/themes', 'Pilih theme TUI'],
    ['/thinking', 'Toggle reasoning mode'],
    ['/undo', 'Undo pesan terakhir'],
    ['/redo', 'Redo pesan'],
    ['/ganti nama', 'Ganti nama profil user'],
    ['/hapus akun', 'Hapus profil user'],
    ['/help', 'Tampilkan semua command yang tersedia'],
    ['/exit, /quit, /q', 'Keluar dari Vexalyn Code'],
    ['Ctrl+P', 'Quick open file (fuzzy finder)'],
    ['chat bebas', 'Kirim pesan ke AI'],
  ];
  const width = Math.max(
    cWidth,
    displayWidth('✦ Slash Commands'),
    ...cmds.map(([cmd, desc]) => displayWidth(`  ${cmd.padEnd(16)}${desc}`)),
  );

  process.stdout.write('\n');
  process.stdout.write(border('╭', width) + '\n');
  process.stdout.write(cardLine(`${BOLD}${PURPLE}✦ Slash Commands${RESET}`, width) + '\n');
  process.stdout.write(border('├', width) + '\n');
  cmds.forEach(([cmd, desc]) => {
    const cmdStr = `${YELLOW}${cmd.padEnd(16)}${RESET}`;
    const descStr = `${GREY}${desc}${RESET}`;
    process.stdout.write(cardLine(`  ${cmdStr}${descStr}`, width) + '\n');
  });
  process.stdout.write(border('╰', width) + '\n\n');
}
